import React, { useEffect, useState } from 'react';
import { Check, X, Trash2 } from 'lucide-react';

const AdminApplications = () => {
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchApps();
    }, []);

    const fetchApps = async () => {
        const token = localStorage.getItem('paradise_token');
        const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:4000';
        try {
            const res = await fetch(`${API_BASE}/api/applications`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                setApps(await res.json());
            } else {
                throw new Error(`HTTP Error: ${res.status} ${res.statusText}`);
            }
        } catch (e) {
            console.error(e);
            setError(e.message || 'Failed to fetch applications');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async (id, status) => {
        if (!window.confirm(`Are you sure you want to ${status} this application?`)) return;

        const token = localStorage.getItem('paradise_token');
        const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:4000';
        try {
            const res = await fetch(`${API_BASE}/api/applications/${id}`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status })
            });

            if (res.ok) {
                fetchApps();
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to DELETE this application? This action cannot be undone.")) return;

        // Optional: Ask if they want to delete the linked creator profile too if approved
        const deleteCreator = window.confirm("Also delete associated Creator Profile (if any)?");

        const token = localStorage.getItem('paradise_token');
        const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:4000';
        try {
            const res = await fetch(`${API_BASE}/api/applications/${id}${deleteCreator ? '?includeCreator=true' : ''}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (res.ok) {
                fetchApps();
            } else {
                alert("Failed to delete application");
            }
        } catch (e) {
            console.error(e);
            alert("Error deleting application");
        }
    };

    return (
        <div>
            <h2 className="text-2xl font-bold mb-6">Applications</h2>
            {loading ? (
                <div>Loading...</div>
            ) : apps.length === 0 ? (
                <div>No pending applications.</div>
            ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-200 text-xs font-bold uppercase tracking-wide text-gray-500">
                                <th className="p-4">Name</th>
                                <th className="p-4">Email</th>
                                <th className="p-4">Links</th>
                                <th className="p-4">Note</th>
                                <th className="p-4">Status</th>
                                <th className="p-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {apps.map(app => (
                                <tr key={app._id} className="border-b border-gray-100 hover:bg-gray-50">
                                    <td className="p-4 font-bold">{app.name}</td>
                                    <td className="p-4 text-sm">{app.email}</td>
                                    <td className="p-4 text-xs space-y-1">
                                        {app.instagram && <div className="truncate text-blue-600 max-w-[150px]">{app.instagram}</div>}
                                        {app.portfolioUrl && <div className="truncate text-blue-600 max-w-[150px]"><a href={app.portfolioUrl} target="_blank" rel="noopener noreferrer">{app.portfolioUrl}</a></div>}
                                    </td>
                                    <td className="p-4 text-sm max-w-xs">{app.note}</td>
                                    <td className="p-4">
                                        <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded ${app.status === 'approved' ? 'bg-green-100 text-green-800' :
                                            app.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                                'bg-yellow-100 text-yellow-800'
                                            }`}>
                                            {app.status}
                                        </span>
                                    </td>
                                    <td className="p-4 text-right">
                                        <div className="flex items-center justify-end space-x-2">
                                            {app.status === 'pending' && (
                                                <>
                                                    <button
                                                        onClick={() => handleUpdateStatus(app._id, 'approved')}
                                                        className="bg-black text-white p-1 rounded hover:bg-green-600"
                                                        title="Approve"
                                                    >
                                                        <Check size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleUpdateStatus(app._id, 'rejected')}
                                                        className="bg-gray-200 text-gray-600 p-1 rounded hover:bg-red-500 hover:text-white"
                                                        title="Reject"
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </>
                                            )}
                                            <button
                                                onClick={() => handleDelete(app._id)}
                                                className="bg-white border border-red-200 text-red-600 p-1 rounded hover:bg-red-50"
                                                title="Delete"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {error && (
                <div className="mt-4 p-4 bg-red-100 text-red-800 rounded border border-red-200">
                    <p className="font-bold">Error loading applications:</p>
                    <p className="text-sm">{error}</p>
                </div>
            )}
        </div >
    );
};

export default AdminApplications;
